/**
 * GlowStatCard Component
 * Animated stat card with accent-colored hover glow
 */

import { cn } from '@utils/cn';
import { motion } from 'framer-motion';
import type { ReactElement, ReactNode } from 'react';

export interface GlowStatCardProps {
  label: string;
  children: ReactNode;
  sub?: string;
  accent?: 'default' | 'success' | 'danger' | 'warning' | 'live';
  className?: string;
}

const accentGlow = {
  default: 'hover:shadow-theme-primary/10',
  success: 'hover:shadow-theme-success/10',
  danger: 'hover:shadow-theme-danger/10',
  warning: 'hover:shadow-theme-warning/10',
  live: 'hover:shadow-theme-danger/20',
};

const accentBorder = {
  default: 'border-theme-border',
  success: 'border-theme-success/30',
  danger: 'border-theme-danger/30',
  warning: 'border-theme-warning/30',
  live: 'border-theme-danger/40',
};

export function GlowStatCard({
  label,
  children,
  sub,
  accent = 'default',
  className,
}: GlowStatCardProps): ReactElement {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        'rounded-xl p-4 border transition-shadow duration-300',
        'bg-theme-surface',
        accentBorder[accent],
        accentGlow[accent],
        'hover:shadow-lg',
        className,
      )}
    >
      <div
        className={cn(
          'text-xs uppercase tracking-wider mb-1.5 font-medium',
          'text-theme-text-tertiary',
        )}
      >
        {label}
      </div>
      <div className={cn('text-2xl font-bold', 'text-theme-text')}>{children}</div>
      {sub && <div className={cn('text-xs mt-1', 'text-theme-text-secondary')}>{sub}</div>}
    </motion.div>
  );
}
